import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "../styleSheets/userGuide.css";

type GuideStep = {
  title: string;
  body: string;
  images: string[];
};

type GuideSection = {
  id: string;
  title: string;
  intro: string;
  steps: GuideStep[];
};

function UserGuide() {
  const { t } = useTranslation("userGuide");
  const sections = t("sections", { returnObjects: true }) as GuideSection[];
  const [activeSection, setActiveSection] = useState<string>(
    sections[0]?.id ?? "",
  );

  const activeSectionIndex = Math.max(
    0,
    sections.findIndex((s) => s.id === activeSection),
  );
  const current = sections.find((s) => s.id === activeSection) ?? sections[0];

  return (
    <div className="ug-page">
      <div className="ug-layout">
        <nav
          className="ug-nav"
          aria-label={t("pageBody.support.userGuide", {
            defaultValue: "User guide",
          })}
        >
          <h2 className="ug-nav__title">{t("contents")}</h2>
          <ul className="ug-nav__list">
            {sections.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  className={`ug-nav__item${activeSection === section.id ? " ug-nav__item--active" : ""}`}
                  onClick={() => setActiveSection(section.id)}
                  aria-pressed={activeSection === section.id}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <section className="ug-main" aria-labelledby="ug-section-title">
          {current && (
            <>
              <div className="ug-section-header">
                <h1 className="ug-section-title" id="ug-section-title">
                  {current.title}
                </h1>
                <p className="ug-section-intro">{current.intro}</p>
              </div>

              <div className="ug-steps">
                {current.steps.map((step, idx) => (
                  <div key={idx} className="ug-step">
                    <div className="ug-step__number">{idx + 1}</div>
                    <div className="ug-step__content">
                      <div className="ug-step__title">{step.title}</div>
                      <p className="ug-step__body">{step.body}</p>
                      {step.images.length > 0 && (
                        <div className="ug-step__images">
                          {step.images.map((src, imgIdx) => (
                            <img
                              key={imgIdx}
                              src={src}
                              alt={`${step.title} — step ${idx + 1} #${imgIdx + 1}`}
                              className="ug-step__image"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="ug-section-nav">
                {activeSectionIndex > 0 && (
                  <button
                    type="button"
                    className="btn btn--white btn--size-md btn--icon-left"
                    onClick={() => {
                      setActiveSection(sections[activeSectionIndex - 1].id);
                    }}
                  >
                    <div
                      className="btn__icon icon-chevron-left"
                      aria-hidden="true"
                    />
                    {sections[activeSectionIndex - 1].title}
                  </button>
                )}
                {activeSectionIndex < sections.length - 1 && (
                  <button
                    type="button"
                    className="btn btn--green btn--size-md ug-section-nav__next"
                    onClick={() => {
                      setActiveSection(sections[activeSectionIndex + 1].id);
                    }}
                  >
                    {sections[activeSectionIndex + 1].title}
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default UserGuide;
